import React, { useCallback, useMemo } from 'react';
import { RefreshControl, ScrollView, TouchableOpacity, View } from 'react-native';
import { useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';

import { PEOPLE_TRAINING_OUTSTANDING_STATUSES, PREDEFINED_USER_ROLES, ROLE_PERMISSIONS } from '../../../common/constants';
import { GET_MY_ROSTER_SHIFTS } from '../../../common/graphql/people/roster';
import { useUserContext } from '../../../common/utils/context';
import { formatShiftRange } from '../../../common/utils/people/roster';
import { getLocalTime } from '../../../common/utils/time/time';
import { formatDate } from '../../../common/utils/utils';
import { GET_MOBILE_TRAINING_ASSIGNMENTS } from '../../graphql/training';
import { Heading, Label, Text } from '../../components';
import { useRefreshPage } from '../../hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import { MY_DAY_TICKET_SCOPE } from './useMyDayMode';
import useMyClockStatus from './useMyClockStatus';

const resolveBadgeCount = (count) => {
	const normalized = Number(count) || 0;
	return normalized > 0 ? normalized : null;
};

const MyDayHubView = ({
	refetchModeData,
	ticketScope = MY_DAY_TICKET_SCOPE.GENERIC,
	onViewEmployeeDay,
	onShowPickingWork,
}) => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { theme } = useTheme();
	const { user, tenant, isAVC, isPicker } = useUserContext();
	const { clockStatus, canUseClockStatus, refetch: refetchClockStatus } = useMyClockStatus();

	const todayDateKey = useMemo(() => getLocalTime(tenant?.timezone).format('YYYY-MM-DD'), [tenant?.timezone]);
	const { data: rosterData, refetch: refetchRosterShifts } = useQuery(GET_MY_ROSTER_SHIFTS, {
		variables: {
			fromDate: todayDateKey,
			toDate: todayDateKey,
		},
		// Roster is AVC people-gated; errors must not block the hub.
		skip: !isAVC,
		errorPolicy: 'all',
	});
	const rosterShift = useMemo(() => (
		(rosterData?.getMyRosterShifts || []).find(shift => shift?.date === todayDateKey) || null
	), [rosterData?.getMyRosterShifts, todayDateKey]);
	// Practical-only list plus the online-training count, so the hub tile can advertise
	// trainings that are completed in Sentry People on the web.
	const { data: trainingData, refetch: refetchTrainings } = useQuery(GET_MOBILE_TRAINING_ASSIGNMENTS, {
		variables: {
			page: 0,
			perPage: 1,
			sortBy: 'dueAt',
			sortDirection: 1,
			filter: { statuses: PEOPLE_TRAINING_OUTSTANDING_STATUSES },
		},
		skip: !isAVC,
		errorPolicy: 'all',
	});
	const refetchMyDay = useCallback(() => {
		const requests = refetchModeData ? [refetchModeData()] : [];
		if (isAVC) {
			requests.push(refetchRosterShifts(), refetchClockStatus(), refetchTrainings());
		}

		return Promise.all(requests);
	}, [isAVC, refetchClockStatus, refetchModeData, refetchRosterShifts, refetchTrainings]);
	const [refreshPage, isRefreshing] = useRefreshPage(refetchMyDay);

	const goToScreen = useCallback(screenName => () => {
		navigation.push(screenName);
	}, [navigation]);

	// Tile visibility mirrors the drawer conditions in NavigationMenu.js.
	const isAdminUser = user && (user.isSuperAdmin || (isAVC && user.role.name === PREDEFINED_USER_ROLES.ADMIN));
	const canViewStockDeliveries = isAdminUser && (user.isSuperAdmin || user.role.permissions.includes(ROLE_PERMISSIONS.VIEW_STOCK_DELIVERY_LIST_PAGE));
	const hasStockPagePermission = user && (user.isSuperAdmin || user.role.permissions.includes(ROLE_PERMISSIONS.VIEW_STOCK_PAGE));
	const canViewPendingStock = Boolean(hasStockPagePermission || isPicker);
	const canViewPurchaseOrders = user && (user.isSuperAdmin || user.role.permissions.includes(ROLE_PERMISSIONS.VIEW_PURCHASE_ORDER_LIST_PAGE));
	const canViewWarehouseMenu = Boolean(isAdminUser || canViewPendingStock || canViewStockDeliveries || canViewPurchaseOrders);
	const canViewTicketList = ticketScope === MY_DAY_TICKET_SCOPE.DRIVER || (user && (
		user.isSuperAdmin || user.role.permissions.includes(ROLE_PERMISSIONS.VIEW_TICKET_LIST_PAGE)
	));

	const trainingBadge = resolveBadgeCount((Number(trainingData?.getTrainingAssignments?.count) || 0) +
		(Number(trainingData?.getTrainingAssignments?.mobileOnlineCount) || 0));

	const tiles = [
		{ key: 'employeeDay', label: 'View an employee\'s day', icon: 'people-outline', visible: Boolean(onViewEmployeeDay), onPress: onViewEmployeeDay },
		{ key: 'pickingWork', label: 'Help with picking', icon: 'basket-outline', visible: Boolean(onShowPickingWork), onPress: onShowPickingWork },
		{ key: 'clockPass', label: 'Shift pass', icon: 'qr-code-outline', visible: Boolean(isAVC), screen: 'ClockPassView' },
		{ key: 'roster', label: 'My roster', icon: 'calendar-outline', visible: Boolean(isAVC), screen: 'MyRosterView' },
		{ key: 'procedures', label: 'Procedures', icon: 'document-text-outline', visible: Boolean(isAVC), screen: 'ProcedureListView' },
		{ key: 'trainings', label: 'Training', icon: 'school-outline', visible: Boolean(isAVC), badge: trainingBadge, screen: 'TrainingListView' },
		{ key: 'tickets', label: 'Tickets', icon: 'ticket-outline', visible: Boolean(canViewTicketList), screen: 'TicketListView' },
		{ key: 'warehouse', label: 'Warehouse', icon: 'cube-outline', visible: canViewWarehouseMenu, screen: 'WarehouseView' },
	].filter(tile => tile.visible);

	const todayDisplayDate = formatDate(getLocalTime(tenant?.timezone), 'DAY_MONTHWORD_YEAR');

	return (
		<ScrollView
			style={styles.container}
			scrollIndicatorInsets={{ right: 1 }}
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refreshPage}
				/>
			)}
		>
			<View style={styles.content}>
				<View style={styles.headerCard}>
					<Heading heading="My day" subHeading={todayDisplayDate} />
					{
						canUseClockStatus && (
							<View style={styles.chipsRow}>
								<Label
									label={clockStatus.isClockedIn ? 'Clocked in' : clockStatus.hasClockedOutToday ? 'Clocked out' : 'Not clocked in'}
									green={clockStatus.isClockedIn}
									secondary={!clockStatus.isClockedIn && clockStatus.hasClockedOutToday}
									yellow={!clockStatus.isClockedIn && !clockStatus.hasClockedOutToday}
									style={styles.chip}
								/>
								{
									Boolean(rosterShift) && (
										<Label
											label={`Shift ${formatShiftRange(rosterShift.startMinutes, rosterShift.endMinutes)}`}
											secondary={true}
											style={styles.chip}
										/>
									)
								}
							</View>
						)
					}
					<Text style={styles.subtleText}>Quick access to your daily tools.</Text>
				</View>
				<View style={styles.tileGrid}>
					{
						tiles.map(tile => (
							<TouchableOpacity
								key={tile.key}
								style={styles.tile}
								activeOpacity={0.86}
								onPress={tile.onPress || goToScreen(tile.screen)}
							>
								{
									tile.badge ? (
										<View style={styles.tileBadge}>
											<Text style={styles.tileBadgeText}>{tile.badge}</Text>
										</View>
									) : null
								}
								<Ionicons name={tile.icon} size={26} color={theme.primary1} />
								<Text style={styles.tileLabel}>{tile.label}</Text>
							</TouchableOpacity>
						))
					}
				</View>
			</View>
		</ScrollView>
	);
};

const createStyles = (theme) => ({
	container: {
		backgroundColor: theme.mainBg4,
		flex: 1,
	},
	content: {
		padding: 10,
		paddingBottom: 30,
	},
	headerCard: {
		backgroundColor: theme.mainBg1,
		borderRadius: 12,
		padding: 12,
		marginBottom: 10,
		borderWidth: 1,
		borderColor: theme.border3,
	},
	chipsRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		marginBottom: 8,
	},
	chip: {
		marginRight: 6,
		marginBottom: 0,
	},
	subtleText: {
		color: theme.text2,
	},
	tileGrid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		marginHorizontal: -4,
	},
	tile: {
		width: '46%',
		flexGrow: 1,
		backgroundColor: theme.mainBg1,
		borderRadius: 12,
		borderWidth: 1,
		borderColor: theme.border3,
		paddingVertical: 20,
		alignItems: 'center',
		margin: 4,
	},
	tileLabel: {
		marginTop: 8,
		fontSize: 14,
		fontWeight: '600',
		color: theme.text1,
	},
	tileBadge: {
		position: 'absolute',
		top: 8,
		right: 8,
		minWidth: 22,
		height: 22,
		borderRadius: 11,
		backgroundColor: '#cd4246',
		alignItems: 'center',
		justifyContent: 'center',
		paddingHorizontal: 6,
	},
	tileBadgeText: {
		color: '#ffffff',
		fontSize: 12,
		fontWeight: '700',
	},
});

export default MyDayHubView;
